// Currently only use in translate -> languages
<template>
  <div class="flex-container">
    <span v-if="mode !== 'form'" class="label-text has-text-grey">
      {{ setting.label || name }}
    </span>
    <el-select v-model="value[name]" multiple filterable class="select-field">
      <el-option
        v-for="item in setting.options"
        :key="getItemKey(item)"
        :label="getItemLabel(item)"
        :value="getItemValue(item)"
      >
        <span :style="{ float: 'left' }">{{ getItemLabel(item) }}</span>
        <span
          v-if="setting.displayValue"
          class="has-text-grey"
          :style="{
            float: 'right',
            fontSize: 13 + 'px',
            marginLeft: '1.5em',
          }"
          >{{ item.code ? item.code : item.name }}</span
        >
      </el-option>
    </el-select>
  </div>
</template>

<script>
/**
 * Multiselect SmartField - Renders a multi-select dropdown for array configuration values.
 *
 * @component
 * @description Displays a filterable multi-select dropdown allowing multiple selections.
 * Supports flexible option formats (name/label, code/value) and can display both
 * the label and code/value for each option. Currently used primarily for language selection.
 *
 * @prop {Object} value - Parent object containing the setting value
 * @prop {Object} setting - Field configuration object
 * @prop {string} [setting.label] - Display label for the select (defaults to name)
 * @prop {Array<Object>} setting.options - Array of options to display
 * @prop {string} [setting.options[].name] - Display name for the option (or use label)
 * @prop {string} [setting.options[].label] - Display label for the option (or use name)
 * @prop {string} [setting.options[].code] - Code/value for the option (or use value)
 * @prop {string} [setting.options[].value] - Value for the option (or use code)
 * @prop {boolean} [setting.displayValue] - If true, shows the code/value alongside the label
 * @prop {Array} [setting.default] - Default array of selected values if not set
 * @prop {string} name - Property key in the value object
 * @prop {string} [mode] - Display mode ('form' hides label)
 *
 * @example
 * <Multiselect
 *   :value="config"
 *   :setting="{
 *     label: 'Languages',
 *     options: [
 *       { name: 'English', code: 'en' },
 *       { name: 'Spanish', code: 'es' }
 *     ],
 *     displayValue: true
 *   }"
 *   name="languages"
 * />
 */
export default {
  props: ["value", "setting", "name", "mode"],
  mounted() {
    if (this.value[this.name] == null) {
      if (this.setting.default != null) {
        this.$set(this.value, this.name, this.setting.default);
      } else {
        this.$set(this.value, this.name, null);
      }
    }
  },
  methods: {
    /**
     * Gets the display label for an option item.
     *
     * @param {Object} item - Option item
     * @returns {string} The label or name
     */
    getItemLabel(item) {
      return item.name || item.label;
    },
    /**
     * Gets the unique key for an option item.
     *
     * @param {Object} item - Option item
     * @returns {string} The code or value
     */
    getItemKey(item) {
      return item.code || item.value;
    },
    /**
     * Gets the value to store for an option item.
     *
     * @param {Object} item - Option item
     * @returns {string} The code/value if available, otherwise the label
     */
    getItemValue(item) {
      return item.code || item.value ? this.getItemKey(item) : this.getItemLabel(item);
    },
  },
};
</script>

<style scoped>
.flex-container {
  display: flex;
  align-items: flex-start;
}

.label-text {
  margin-left: 0.5em;
  margin-right: 3.5em;
  font-size: 14px;
}

.select-field {
  width: 100%;
}
</style>
